/**
 * Shrink report client.
 *
 * Reads the existing inventory shrink endpoints. Totals come from the summary
 * payload; this module does not aggregate events.
 */

import { api } from '../services/api';

/** Matches the unfiltered GET /inventory/shrink default page size. */
export const SHRINK_PAGE_SIZE = 20;

export interface ShrinkRecord {
  id: string;
  product_id: string;
  quantity: number;
  reason: string;
  order_id: string | null;
  created_by: string;
  created_at: string;
  notes: string | null;
}

export interface ShrinkListResponse {
  records: ShrinkRecord[];
  total: number;
  skip: number;
  limit: number;
  month: number | null;
  year: number | null;
}

export interface ShrinkReasonSummary {
  reason: string;
  count: number | string;
  total_qty: number | string;
}

export interface ShrinkSummary {
  total_quantity: number | string;
  total_records: number | string;
  by_reason: ShrinkReasonSummary[];
}

export interface ShrinkSummaryResponse {
  summary: ShrinkSummary;
  actor: string;
}

export interface ShrinkListQuery {
  month?: number;
  year?: number;
  skip?: number;
  limit?: number;
}

/**
 * Query string for GET /inventory/shrink.
 *
 * The API applies the month filter only when both `month` and `year` are set.
 * In that mode it returns the whole month and ignores skip/limit. Otherwise it
 * returns a newest-first page.
 */
export function shrinkListQuery(input: {
  month: number | null;
  year: number | null;
  skip: number;
  limit?: number;
}): ShrinkListQuery {
  if (input.month != null && input.year != null) {
    return { month: input.month, year: input.year };
  }
  return {
    skip: input.skip,
    limit: input.limit ?? SHRINK_PAGE_SIZE,
  };
}

/** Parse `<input type="month">` (`YYYY-MM`) into the API's month and year. */
export function parseShrinkMonth(value: string): { month: number; year: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { month, year };
}

export async function fetchShrinkRecords(query: ShrinkListQuery): Promise<ShrinkListResponse> {
  const response = await api.get<ShrinkListResponse>('/inventory/shrink', { params: query });
  return response.data;
}

export async function fetchShrinkSummary(): Promise<ShrinkSummaryResponse> {
  const response = await api.get<ShrinkSummaryResponse>('/inventory/shrink/summary');
  return response.data;
}
